import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, Subject, switchMap, takeUntil, tap } from "rxjs";
import { AppConfigService } from "src/app/core";
import { HelperMethods } from "src/app/shared";
import { DesignLayout } from "../../design-layouts/models";
import { GetVenueTemplatesResponse, VenueAdminResponses } from "../responses/venue-admin-responses";
import { VenueAdminTemplatesPresetsFilterService } from "./venue-admin-templates-presets-filter.service";
import { VenueAdminService } from "./venue-admin.service";

export const LAYOUT_PAGE_COUNT: number = 24;

interface UpdateItemsInput {
  page?: number;
  reset?: boolean;
}

export interface LayoutsState {
  loading?: boolean;
  failed?: boolean;
  layouts?: DesignLayout[];
  errorMessage?: string;
  totalCount?: number;
}

@Injectable()
export class VenueAdminTemplatesPresetsLayoutService implements OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();
  private _updateItems$: Subject<UpdateItemsInput> = new Subject<UpdateItemsInput>();

  private _layoutsStateSubject$: BehaviorSubject<LayoutsState> = new BehaviorSubject(null);
  layoutsState$: Observable<LayoutsState> = this._layoutsStateSubject$.asObservable();

  constructor(
    private _venueAdminTemplatesPresetsFilterService: VenueAdminTemplatesPresetsFilterService,
    private _http: HttpClient,
    private _appConfigService: AppConfigService,
    private _venueAdminService: VenueAdminService,
  ) {
    this.initLayouts();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  initLayouts(): void {
    this._updateItems$
      .pipe(
        tap(input => {
          this._layoutsStateSubject$.next({
            failed: false,
            loading: true,
            layouts: [],
            totalCount: input.reset ? 0 : this._getTotalItemCount(),
          });
        }),
        switchMap(input => {
          return this.getLayouts$(input.page).pipe(
            tap(response => {
              const layouts = VenueAdminResponses.getVenueTemplatesFromResponse(response);

              this._layoutsStateSubject$.next({
                failed: false,
                loading: false,
                layouts: layouts,
                totalCount: response.totalCount,
              });
            }),
            catchError(error => {
              this._layoutsStateSubject$.next({
                failed: true,
                loading: false,
                layouts: null,
                totalCount: null,
                errorMessage: HelperMethods.getResponseErrors(error)[0],
              });
              return of(null);
            }),
          );
        }),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this._venueAdminTemplatesPresetsFilterService.filtersChanged$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: () => {
        this._updateItems$.next({ page: 0, reset: true });
      },
    });
  }

  setPageNumber(page: number): void {
    this._updateItems$.next({ page: page });
  }

  getLayouts$(pageNumber: number): Observable<GetVenueTemplatesResponse> {
    return this._http.get<GetVenueTemplatesResponse>(
      this._appConfigService.getVenueAdminTemplatesPresetsTemplatesUrl(
        this._venueAdminService.activeVenue.id,
        LAYOUT_PAGE_COUNT,
        pageNumber + 1,
        this._venueAdminTemplatesPresetsFilterService.buildFiltersArray(),
      ),
    );
  }

  private _getTotalItemCount(): number {
    return this._layoutsStateSubject$.value?.totalCount ?? 0;
  }
}
